import { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { 
  Sparkles, 
  ShoppingBag, 
  Package, 
  TreePine, 
  PartyPopper, 
  Clock, 
  Wrench, 
  AlertTriangle,
  Users,
  MessageSquare,
  ChevronDown,
  Check
} from "lucide-react";

const OptionalServices = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [expandedService, setExpandedService] = useState(null);

  const services = [
    {
      id: 1,
      icon: Sparkles,
      title: "Pre-Arrival Luxury Preparation",
      price: "$400 – $750 per visit",
      description: "Comprehensive preparation to ensure your home is fully ready before arrival.",
      includes: [
        "Climate and lighting calibration",
        "Linen staging and refresh",
        "Fireplace preparation",
        "Kitchen and living space presentation reset",
        "Final walkthrough prior to arrival"
      ]
    },
    {
      id: 2,
      icon: ShoppingBag,
      title: "Curated Grocery & Provisioning",
      price: "$250 – $500 per visit",
      description: "Personalized grocery coordination and in-home organization.",
      includes: [
        "Custom grocery list planning",
        "In-store shopping or delivery coordination",
        "Full pantry and refrigerator setup",
        "Beverage and specialty item placement"
      ],
      note: "Groceries billed separately."
    },
    {
      id: 3,
      icon: Package,
      title: "Move-In / Move-Out Coordination",
      price: "$350 – $750 per visit",
      description: "Full support for property transitions.",
      includes: [
        "Full property walkthrough",
        "Utility and systems verification",
        "Vendor coordination as needed",
        "Presentation reset",
        "Access and key coordination"
      ]
    },
    {
      id: 4,
      icon: TreePine,
      title: "Holiday & Seasonal Styling",
      price: "$750 – $2,500+",
      description: "Professional installation and seasonal presentation preparation.",
      includes: [
        "Décor installation",
        "Seasonal staging",
        "Vendor coordination",
        "Scheduled removal (billed separately or packaged)"
      ],
      note: "Décor materials billed separately."
    },
    {
      id: 5,
      icon: PartyPopper,
      title: "Special Event Coordination",
      price: "Starting at $1,500",
      description: "Private dinner parties, celebrations, and intimate gatherings.",
      includes: [
        "Vendor sourcing",
        "Timeline planning",
        "Setup oversight",
        "On-site coordination (if required)"
      ],
      note: "Event vendors billed separately."
    },
    {
      id: 6,
      icon: Clock,
      title: "Personalized Concierge Services",
      price: "$150 – $225 per hour",
      description: "Custom requests tailored to your needs.",
      includes: [
        "Restaurant and activity reservations",
        "Transportation arrangements",
        "Private chef and wellness scheduling",
        "Special sourcing requests",
        "Equipment delivery coordination"
      ]
    },
    {
      id: 7,
      icon: Wrench,
      title: "Construction or Project Visit",
      price: "$200 – $350 per site visit",
      description: "Non-Retainer on-site support.",
      includes: [
        "On-site documentation",
        "Contractor coordination",
        "Progress reporting"
      ]
    },
    {
      id: 8,
      icon: AlertTriangle,
      title: "Emergency / After-Hours Response",
      price: "$250 – $350 per hour",
      description: "Applies outside of retainer coverage.",
      includes: [
        "Immediate response coordination",
        "Emergency vendor dispatch",
        "Situation assessment and resolution"
      ]
    },
    {
      id: 9,
      icon: Users,
      title: "Vendor Coordination",
      price: "15–20% coordination fee",
      description: "When Alpine Property Concierge manages third-party service providers.",
      includes: [
        "Vendor sourcing and vetting",
        "Scheduling coordination",
        "Quality oversight",
        "Fee varies based on scope and complexity"
      ]
    }
  ];

  return (
    <section
      ref={sectionRef}
      className="py-24 md:py-32 bg-white"
      data-testid="optional-services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="text-accent text-sm font-semibold tracking-wider uppercase">
            On-Demand Services
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy mt-4 mb-6 leading-tight">
            Flexible Support for <span className="italic">Every Need</span>
          </h2>
          <p className="text-gray-600 text-base md:text-lg">
            In addition to structured Estate Stewardship, Alpine Property Concierge offers flexible, as-needed services designed to support seamless arrivals, elevated stays, and personalized coordination.
          </p>
          <p className="text-gray-500 text-sm mt-4 italic">
            All services are scheduled in advance and subject to availability.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((service, index) => {
            const isExpanded = expandedService === service.id;
            
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.05 * index }}
                className="bg-gray-50 rounded-xl p-5 hover:shadow-md transition-shadow duration-300"
                data-testid={`optional-service-${index}`}
              >
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                    <service.icon className="w-5 h-5 text-navy" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-heading text-base text-navy">{service.title}</h4>
                    <p className="text-accent font-semibold text-sm">{service.price}</p>
                  </div>
                </div>

                {/* View More Button */}
                <button
                  onClick={() => setExpandedService(isExpanded ? null : service.id)}
                  className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-navy transition-colors mt-2"
                  data-testid={`view-more-${service.id}`}
                >
                  <span>{isExpanded ? 'Hide Details' : 'View More'}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>

                {/* Expandable Content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 mt-3 border-t border-gray-200">
                        <p className="text-gray-600 text-sm mb-3">{service.description}</p>
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Includes:</p>
                        <ul className="space-y-1">
                          {service.includes.map((item) => (
                            <li key={item} className="flex items-start gap-2 text-xs text-gray-600">
                              <Check className="w-3 h-3 text-accent flex-shrink-0 mt-0.5" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                        {service.note && (
                          <p className="text-xs text-gray-500 italic mt-2">{service.note}</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Custom Requests */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-12 p-8 bg-navy/5 rounded-2xl text-center"
        >
          <MessageSquare className="w-10 h-10 text-navy mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-heading text-xl text-navy mb-2">Custom Requests Welcome</h3>
          <p className="text-gray-600 text-sm mb-6">
            If a requested service is not listed, we are pleased to discuss tailored arrangements.
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center justify-center bg-navy text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-navy-light transition-all duration-300"
            data-testid="custom-request-btn"
          >
            Request Consultation
          </a>
        </motion.div>

        {/* Pricing Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-200 text-center"
        >
          <p className="text-gray-500 text-sm italic">
            Prices listed are starting estimates and may vary depending on property size, service scope, and specific homeowner needs. Final pricing will be confirmed after consultation.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default OptionalServices;
